import React, {
  createContext,
  useContext,
  useReducer,
  useMemo,
  useRef,
} from 'react';
import { windowReducer, initialWindowState } from './windowReducer';

// =============================================================================
// WindowManagerContext — global state + imperative API for the IM window
// manager. Split into TWO contexts so consumers subscribe to only what they
// need:
//
//   StateContext  — the window registry (changes every open/move/focus). The
//                   WindowLayer + Taskbar subscribe to this and re-render.
//   ApiContext    — the imperative API (openWindow/closeWindow/…). STABLE for
//                   the lifetime of the provider, so callers (App.js) that only
//                   dispatch never re-render when window state changes.
//
// The content of a window is an arbitrary React node, so the manager stays
// generic: it knows nothing about IM/chat.
// =============================================================================

const StateContext = createContext(initialWindowState);
const ApiContext = createContext(null);

export const WindowManagerProvider = ({ children }) => {
  const [state, dispatch] = useReducer(windowReducer, initialWindowState);

  // Keep a live ref to state so API callbacks can read the latest registry
  // without being re-created (and without re-rendering their consumers).
  const stateRef = useRef(state);
  stateRef.current = state;

  // Monotonic id source for windows opened WITHOUT an explicit id. (Explicit
  // ids — e.g. `im:<userId>` — let callers dedupe/update a known window.)
  const autoIdRef = useRef(0);

  const api = useMemo(() => {
    // openWindow(render, options) -> id
    //   render : (props) => ReactNode      content factory
    //   options: { id?, title?, props?, width?, height?, x?, y?,
    //              minWidth?, minHeight?, maximizable?, minimizable? }
    const openWindow = (render, options = {}) => {
      const id = options.id || `win_${++autoIdRef.current}`;
      dispatch({
        type: 'open',
        id,
        title: options.title,
        render,
        props: options.props,
        options: { ...options, viewportW: typeof window !== 'undefined' ? window.innerWidth : 1280 },
      });
      return id;
    };

    const closeWindow = (id) => dispatch({ type: 'close', id });
    const minimizeWindow = (id) => dispatch({ type: 'minimize', id });
    const restoreWindow = (id) => {
      dispatch({ type: 'restore', id });
      dispatch({ type: 'focus', id });
    };
    const maximizeWindow = (id) => dispatch({ type: 'maximize', id });
    const focusWindow = (id) => dispatch({ type: 'focus', id });
    const setRect = (id, rect) => dispatch({ type: 'setRect', id, rect });
    // updateWindow(id, props, title?, options?) — merge content props, an
    // optional title, and optional option overrides (e.g. { attention }).
    const updateWindow = (id, props, title, options) =>
      dispatch({ type: 'update', id, props, title, options });

    // Whether a window with this id is currently open.
    const hasWindow = (id) => !!stateRef.current.windows[id];
    // Whether this window is the focused one AND visible (not minimized).
    const isFocused = (id) =>
      stateRef.current.focusedId === id && !stateRef.current.windows[id]?.minimized;

    return {
      openWindow,
      closeWindow,
      minimizeWindow,
      restoreWindow,
      maximizeWindow,
      focusWindow,
      setRect,
      updateWindow,
      hasWindow,
      isFocused,
    };
  }, []);

  return (
    <ApiContext.Provider value={api}>
      <StateContext.Provider value={state}>{children}</StateContext.Provider>
    </ApiContext.Provider>
  );
};

// Subscribe to the full window registry (re-renders on any window change).
export const useWindowState = () => useContext(StateContext);

// Get the stable imperative API. Never causes a re-render on window-state change.
export const useWindowManager = () => {
  const api = useContext(ApiContext);
  if (!api) {
    throw new Error('useWindowManager must be used within a WindowManagerProvider');
  }
  return api;
};

// Convenience: subscribe to just one window's state (or null). Re-renders only
// when that window object identity changes.
export const useWindow = (id) => {
  const state = useWindowState();
  return id ? state.windows[id] || null : null;
};

// Internal helper for the layer: a stable callback set bound to one window id,
// so each <Window> gets memoizable handlers. Not exported for app use.
export const useWindowHandlers = (id) => {
  const api = useWindowManager();
  return useMemo(
    () => ({
      close: () => api.closeWindow(id),
      minimize: () => api.minimizeWindow(id),
      maximize: () => api.maximizeWindow(id),
      focus: () => api.focusWindow(id),
      setRect: (rect) => api.setRect(id, rect),
    }),
    [api, id]
  );
};

export { StateContext, ApiContext };
